import React from 'react';
import { Stack, Typography } from '@mui/material';
import { UserAvatar } from '@/components/UserAvatar';
import {
	formatDateTime,
	getDateSortValue,
} from '@/components/views/SettingsView/BrokerageUsersTable/helpers';
import { BrokerGroupChip } from '../../shared/chips/BrokerGroupChip';
import { lastUpdatedByColumn } from '../../shared/lastUpdatedByColumn';
import { createdAtColumn, createdByColumn } from '../../shared/createdColumns';
import { BrokerGroupChipsCell } from '../../shared/chips/BrokerGroupChipsCell';
import { getOtherBrokerGroups, getPrimaryBrokerGroup, isUserActive } from '../api/usersApi';
import { UserActiveSwitch } from './UserActiveSwitch';

const insightIdFields = { width: 150 };

// Unset means no; shown and exported as Yes / No
const booleanFields = {
	width: 160,
	valueGetter: (value) => Boolean(value),
	valueFormatter: (value) => (value ? 'Yes' : 'No'),
};

/**
 * Columns for the admin users list (read only, sorted in the browser). The broker group
 * columns sort and export by name (from the user's brokerGroups, see listAllUsers) and show
 * chips by id: Broker group is the primary, Broker groups the others (brokerGroupArray
 * without the primary). Insight ids
 * show as they're stored, since their names are per ledger.
 */
export const userColumns = [
	{
		field: 'name',
		headerName: 'Name',
		flex: 1,
		minWidth: 180,
		hideable: false,
		display: 'flex',
		// Laid out like the CRM tables' owner cell (OwnerCellData), from the row's own name
		renderCell: ({ row }) => (
			<Stack direction='row' spacing={1} alignItems='center' width='100%'>
				<UserAvatar userId={row.id} />
				<Typography color='text.primary'>{row.name}</Typography>
			</Stack>
		),
	},
	{
		field: 'id',
		headerName: 'Id',
		width: 220,
	},
	{
		field: 'isActive',
		headerName: 'Active',
		width: 110,
		// Unset counts as active; exported as Active / Inactive
		valueGetter: (value, row) => isUserActive(row),
		valueFormatter: (value) => (value ? 'Active' : 'Inactive'),
		renderCell: ({ row }) => <UserActiveSwitch user={row} />,
	},
	{
		field: 'email',
		headerName: 'Email',
		flex: 1,
		minWidth: 220,
	},
	{
		field: 'displayEmail',
		headerName: 'Display email',
		minWidth: 220,
		// Some older PROD users have { name, signature } here instead of an email
		valueGetter: (value) => (typeof value === 'string' ? value : (value?.name ?? '')),
	},
	{
		// Shown, sorted and exported as one comma-separated list; rows grow to fit it
		field: 'delegatedEmails',
		headerName: 'Delegated emails',
		minWidth: 260,
		valueGetter: (value) => (value ?? []).join(', '),
		renderCell: ({ value }) => (
			<Typography variant='body2' sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
				{value}
			</Typography>
		),
	},
	{
		field: 'phoneNumber',
		headerName: 'Phone',
		width: 150,
	},
	{
		field: 'role',
		headerName: 'Role',
		width: 160,
	},
	{
		field: 'primaryBrokerGroup',
		headerName: 'Broker group (primary)',
		flex: 1,
		minWidth: 180,
		display: 'flex',
		valueGetter: (value, row) => getPrimaryBrokerGroup(row)?.name,
		renderCell: ({ row }) =>
			row.primaryBrokerGroupId && <BrokerGroupChip id={row.primaryBrokerGroupId} />,
	},
	{
		field: 'otherBrokerGroups',
		headerName: 'Broker groups',
		flex: 1.5,
		minWidth: 220,
		sortable: false,
		display: 'flex',
		valueGetter: (value, row) =>
			getOtherBrokerGroups(row)
				.map(({ name }) => name)
				.join(', '),
		renderCell: ({ row }) => (
			<BrokerGroupChipsCell brokerGroupIds={getOtherBrokerGroups(row).map(({ id }) => id)} />
		),
	},
	{
		field: 'lastUpdated',
		headerName: 'Last updated',
		width: 190,
		type: 'number',
		headerAlign: 'left',
		align: 'left',
		valueGetter: (value) => getDateSortValue(value),
		valueFormatter: (value, row) => formatDateTime(row.lastUpdated),
	},
	lastUpdatedByColumn,
	createdAtColumn,
	createdByColumn,
	{ field: 'salesTeamId', headerName: 'Sales team id', ...insightIdFields },
	{ field: 'serviceTeamId', headerName: 'Service team id', ...insightIdFields },
	{ field: 'accountTypeId', headerName: 'Account type id', ...insightIdFields },
	{ field: 'authorisedRepId', headerName: 'Authorised rep id', ...insightIdFields },
	{ field: 'canViewGroup', headerName: 'Can view group', ...booleanFields },
	{ field: 'principleUser', headerName: 'Principal user', ...booleanFields },
	{ field: 'isNetworkAdmin', headerName: 'Network admin', ...booleanFields },
	{ field: 'isFolioAdmin', headerName: 'Folio admin', ...booleanFields },
	{ field: 'complianceManager', headerName: 'Compliance manager', ...booleanFields },
];

export const DEFAULT_HIDDEN_USER_COLUMNS = [
	'displayEmail',
	'delegatedEmails',
	'role',
	'lastUpdatedBy',
	'createdAt',
	'createdBy',
	'salesTeamId',
	'serviceTeamId',
	'accountTypeId',
	'authorisedRepId',
	'canViewGroup',
	'principleUser',
	'isNetworkAdmin',
	'isFolioAdmin',
	'complianceManager',
];
